import type { TokenEntry } from '@/lib/designTokenParser';
import { updateTokenInMarkdown } from '@/lib/designTokenParser';

interface DimensionTokenGroupProps {
  title: string;
  tokens: TokenEntry[];
  markdown: string;
  onMarkdownChange: (md: string) => void;
  /** Controls visual preview style */
  previewType: 'spacing' | 'borderRadius' | 'value';
}

const cardStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-card-surface)',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  padding: '2px 6px',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '11px',
  fontFamily: 'monospace',
  boxSizing: 'border-box',
  width: '100px',
};

export function DimensionTokenGroup({ title, tokens, markdown, onMarkdownChange, previewType }: DimensionTokenGroupProps) {
  if (tokens.length === 0) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', marginTop: 0 }}>
        {title}
      </h4>
      {tokens.map((token) => (
        <div key={`${token.lineIndex}-${token.name}`} style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', minWidth: '100px' }}>
              {token.name}
            </div>
            <input
              style={inputStyle}
              value={token.value}
              onChange={(e) => {
                onMarkdownChange(updateTokenInMarkdown(token.lineIndex, e.target.value, markdown));
              }}
            />
            <div style={{ flex: 1 }}>
              <DimensionPreview value={token.value} type={previewType} />
            </div>
            {token.comment && (
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', flexShrink: 0, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={token.comment}>
                {token.comment}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DimensionPreview({ value, type }: { value: string; type: 'spacing' | 'borderRadius' | 'value' }) {
  if (type === 'spacing') {
    return (
      <div style={{
        height: '12px',
        width: value,
        maxWidth: '100%',
        backgroundColor: 'var(--color-primary)',
        borderRadius: '2px',
        opacity: 0.6,
      }} />
    );
  }

  if (type === 'borderRadius') {
    return (
      <div style={{
        width: '40px',
        height: '40px',
        backgroundColor: '#dbeafe',
        border: '2px solid var(--color-primary)',
        borderRadius: value,
      }} />
    );
  }

  // 'value' type — just show the value as text
  return (
    <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{value}</span>
  );
}
